/* ZERO Footwear — Cart drawer enhancements
 * - Loads "The perfect combination" cross-sell recommendations
 * - +ADD with inline variant picker (Color / Size) for multi-variant products
 * Loaded as a deferred module after cart-drawer.js / cart.js
 */
(function () {
  'use strict';

  const RECS_LIMIT = 3;
  const RECS_INTENT = 'related';

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  function escAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function plusSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
  }

  function uniquePreserve(arr) {
    const seen = new Set();
    const out = [];
    for (const x of arr) {
      if (!seen.has(x)) {
        seen.add(x);
        out.push(x);
      }
    }
    return out;
  }

  function renderUpsellCard(p) {
    if (!p.variants || p.variants.length === 0) return '';
    const firstVariant = p.variants[0];
    const hasMultipleVariants = p.variants.length > 1;
    const minPrice = Math.min(...p.variants.map((v) => v.price));
    const compareAt = firstVariant.compare_at_price;
    const onSale = compareAt && compareAt > minPrice;
    const savePct = onSale ? Math.round(((compareAt - minPrice) / compareAt) * 100) : 0;
    const img = (p.featured_image && (p.featured_image.url || p.featured_image)) || '';
    const optionNames = p.options || [];

    let pickerHTML = '';
    if (hasMultipleVariants && optionNames.length > 0) {
      const optionDropdowns = optionNames
        .map((option, idx) => {
          // Shopify recommendations endpoint returns options as objects { name, position, values }
          // older callers may pass strings — handle both.
          const optionName =
            typeof option === 'string'
              ? option
              : (option && (option.name || option.title)) || `Option ${idx + 1}`;
          const optionKey = `option${idx + 1}`;
          const values = uniquePreserve(p.variants.map((v) => v[optionKey]));
          const opts = values
            .map((v) => `<option value="${escAttr(v)}">${escAttr(v)}</option>`)
            .join('');
          return `<select class="zero-cart__upsell-option" data-option-index="${idx}" aria-label="${escAttr(optionName)}"><option value="" disabled selected>${escAttr(optionName)}</option>${opts}</select>`;
        })
        .join('');
      pickerHTML = `
        <div class="zero-cart__upsell-picker" hidden>
          <div class="zero-cart__upsell-option-row">${optionDropdowns}</div>
          <div class="zero-cart__upsell-option-row">
            <button class="zero-cart__upsell-confirm" type="button" disabled>${plusSvg()} ADD TO CART</button>
            <button class="zero-cart__upsell-cancel" type="button">cancel</button>
          </div>
          <div class="zero-cart__upsell-error" role="alert"></div>
        </div>
      `;
    }

    return `
      <div class="zero-cart__upsell-card" data-product-id="${escAttr(p.id)}" data-variants="${escAttr(JSON.stringify(p.variants))}">
        <a href="${escAttr(p.url)}" tabindex="-1" aria-hidden="true">
          ${img ? `<img src="${escAttr(img)}" alt="${escAttr(p.title)}" loading="lazy" width="60" height="60">` : '<div></div>'}
        </a>
        <div class="zero-cart__upsell-info">
          <a href="${escAttr(p.url)}" class="zero-cart__upsell-name">${escAttr(p.title)}</a>
          <div class="zero-cart__upsell-prices">
            <span class="zero-cart__upsell-price-current">${money(minPrice)}</span>
            ${onSale ? `<s class="zero-cart__upsell-price-compare">${money(compareAt)}</s>` : ''}
            ${onSale ? `<span class="zero-cart__upsell-save">Save ${savePct}%</span>` : ''}
          </div>
          ${pickerHTML}
        </div>
        <button class="zero-cart__upsell-add" type="button" data-variant-id="${hasMultipleVariants ? '' : firstVariant.id}" data-has-picker="${hasMultipleVariants ? 'true' : 'false'}" aria-label="Add ${escAttr(p.title)} to cart">
          ${plusSvg()} ADD
        </button>
      </div>
    `;
  }

  async function fetchRecommendations(productId) {
    const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&intent=${RECS_INTENT}&limit=${RECS_LIMIT}`;
    const resp = await fetch(url);
    if (!resp.ok) return [];
    const data = await resp.json();
    return (data && data.products) || [];
  }

  async function loadCrossSells(container) {
    if (!container) return;
    const seedProductId = container.getAttribute('data-seed-product-id');
    if (!seedProductId) return;
    container.dataset.loading = '1';
    try {
      const products = await fetchRecommendations(seedProductId);
      if (!products.length) {
        container.style.display = 'none';
        return;
      }
      const list = container.querySelector('.zero-cart__upsells-list');
      if (list) list.innerHTML = products.map(renderUpsellCard).join('');
    } catch (e) {
      console.warn('[zero-cart] recommendations failed', e);
      container.style.display = 'none';
    } finally {
      delete container.dataset.loading;
    }
  }

  function findVariant(card) {
    let variants;
    try {
      variants = JSON.parse(card.dataset.variants);
    } catch (_) {
      return null;
    }
    const selects = card.querySelectorAll('.zero-cart__upsell-option');
    const selected = Array.from(selects).map((s) => s.value);
    if (selected.some((v) => !v)) return null;
    return (
      variants.find((v) =>
        selected.every((val, idx) => v[`option${idx + 1}`] === val)
      ) || null
    );
  }

  function showError(card, message) {
    const el = card.querySelector('.zero-cart__upsell-error');
    if (!el) return;
    el.textContent = message;
    el.classList.add('visible');
  }

  function clearError(card) {
    const el = card.querySelector('.zero-cart__upsell-error');
    if (el) {
      el.textContent = '';
      el.classList.remove('visible');
    }
  }

  function expandPicker(card, addBtn) {
    const picker = card.querySelector('.zero-cart__upsell-picker');
    if (!picker) return;
    picker.hidden = false;
    addBtn.style.display = 'none';
    card.classList.add('is-picking');
  }

  function collapsePicker(card) {
    const picker = card.querySelector('.zero-cart__upsell-picker');
    const addBtn = card.querySelector('.zero-cart__upsell-add');
    if (picker) picker.hidden = true;
    if (addBtn) addBtn.style.display = '';
    card.classList.remove('is-picking');
    clearError(card);
  }

  async function addVariantToCart(variantId, button) {
    if (!variantId) return;
    button && (button.disabled = true);
    try {
      const sectionsToFetch = [];
      const cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl && cartDrawerEl.constructor.getSectionsToRender) {
        cartDrawerEl.constructor.getSectionsToRender().forEach((s) => sectionsToFetch.push(s.section));
      } else {
        sectionsToFetch.push('cart-drawer', 'cart-icon-bubble');
      }
      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sectionsToFetch.join(','),
        sections_url: window.location.pathname,
      });
      const resp = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body,
      });
      const data = await resp.json();
      if (data.status) {
        console.warn('[zero-cart] add failed', data.message);
        return;
      }
      if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
        cartDrawerEl.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.warn('[zero-cart] add error', e);
    } finally {
      button && (button.disabled = false);
    }
  }

  function bindUpsellEvents(root) {
    root.addEventListener('click', (e) => {
      const addBtn = e.target.closest('.zero-cart__upsell-add');
      if (addBtn) {
        e.preventDefault();
        const card = addBtn.closest('.zero-cart__upsell-card');
        if (!card) return;
        const hasPicker = addBtn.dataset.hasPicker === 'true';
        if (hasPicker) {
          expandPicker(card, addBtn);
        } else {
          addVariantToCart(addBtn.dataset.variantId, addBtn);
        }
        return;
      }

      const cancelBtn = e.target.closest('.zero-cart__upsell-cancel');
      if (cancelBtn) {
        e.preventDefault();
        const card = cancelBtn.closest('.zero-cart__upsell-card');
        if (card) collapsePicker(card);
        return;
      }

      const confirmBtn = e.target.closest('.zero-cart__upsell-confirm');
      if (confirmBtn) {
        e.preventDefault();
        const card = confirmBtn.closest('.zero-cart__upsell-card');
        if (!card) return;
        clearError(card);
        const variant = findVariant(card);
        if (!variant) {
          showError(card, 'Please select all options.');
          return;
        }
        if (!variant.available) {
          showError(card, 'That combination is sold out — try another.');
          return;
        }
        addVariantToCart(variant.id, confirmBtn);
        return;
      }
    });

    // Capture phase so we run BEFORE the event bubbles up to
    // <cart-drawer-items>, whose Shopify cart.js change handler would
    // otherwise try to validate the upsell dropdown as a quantity input
    // and surface the "increments of undefined" warning.
    root.addEventListener(
      'change',
      (e) => {
        const select = e.target.closest('.zero-cart__upsell-option');
        if (!select) return;
        const card = select.closest('.zero-cart__upsell-card');
        if (card) {
          clearError(card);
          const selects = card.querySelectorAll('.zero-cart__upsell-option');
          const allSelected = Array.from(selects).every((s) => s.value);
          const confirmBtn = card.querySelector('.zero-cart__upsell-confirm');
          if (confirmBtn) confirmBtn.disabled = !allSelected;
          if (allSelected) {
            const variant = findVariant(card);
            if (variant && !variant.available) {
              showError(card, 'That combination is sold out — try another.');
            }
          }
        }
        e.stopPropagation();
      },
      true
    );
  }

  function init() {
    const container = document.querySelector('.zero-cart__upsells');
    if (container) loadCrossSells(container);
    const root = document.querySelector('cart-drawer');
    if (root) bindUpsellEvents(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reload cross-sells whenever the cart drawer re-renders (cart.js replaces innerHTML)
  const observer = new MutationObserver(() => {
    const container = document.querySelector('.zero-cart__upsells');
    if (container && !container.dataset.loaded && !container.dataset.loading) {
      container.dataset.loaded = '1';
      loadCrossSells(container);
    }
  });
  const drawerEl = document.querySelector('cart-drawer');
  if (drawerEl) observer.observe(drawerEl, { childList: true, subtree: true });

  /* ---------------------------------------------------------------
   * Homepage Add-to-cart interceptor
   * Some sections (e.g. legacy Instant Page Builder cards) render
   * Add-to-cart as a plain anchor pointing at the cart permalink:
   *   <a href="/cart/{variantId}:1?storefront=true">Add to cart</a>
   * That URL adds the item server-side and redirects to /cart.
   * Intercept and AJAX-add instead so the drawer opens.
   * --------------------------------------------------------------- */
  const ADD_LINK_SELECTOR =
    'a[data-instant-action-type="redirect-to-cart"], a[href*="storefront=true"]';

  function variantIdFromAnchor(anchor) {
    const dataId = anchor.getAttribute('data-instant-action-variant-id');
    if (dataId) return dataId;
    const href = anchor.getAttribute('href') || '';
    // pattern: /cart/{variant_id}:{quantity}?...
    const m = href.match(/\/cart\/(\d+):/);
    return m ? m[1] : '';
  }

  // Use CAPTURE phase + stopImmediatePropagation so we beat any
  // page-builder app's own click handler that might also be listening.
  document.addEventListener(
    'click',
    (e) => {
      const anchor = e.target.closest(ADD_LINK_SELECTOR);
      if (!anchor) return;
      if (anchor.getAttribute('data-instant-disabled') === 'true') return;
      const variantId = variantIdFromAnchor(anchor);
      if (!variantId) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      addVariantToCart(variantId, anchor);
    },
    true
  );
})();

/* ---------------------------------------------------------------
 * Save-your-cart email capture (2026-10-19)
 * Klaviyo only records "Added to Cart" for shoppers it already knows, so anonymous add-to-carts
 * (most of them) could never get a cart reminder. For shoppers Klaviyo does NOT know, the drawer shows
 * [data-zero-save-cart]. Submit = klaviyo.identify + subscribe to Email List (single opt-in) +
 * klaviyo.track('Saved Cart') with the exact properties the cart-reminder templates read
 * (Product Name, ImageURL, URL, Price, CompareAtPrice, Quantity, Variant Name, VariantID, extra.checkout_url)
 * -> Klaviyo flow SW7dby. If klaviyo.js is blocked/slow, the box simply stays hidden.
 * --------------------------------------------------------------- */
(function () {
  const COMPANY_ID = 'VpFPX6';
  const EMAIL_LIST_ID = 'UuL29L';
  const DONE_KEY = 'zeroCartSaved';
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const withTimeout = (p, ms) => Promise.race([p, sleep(ms).then(() => { throw new Error('timeout'); })]);
  let knownState = null; // null = unknown, true = Klaviyo knows the shopper, false = anonymous

  function saved() {
    try { return sessionStorage.getItem(DONE_KEY) === '1'; } catch (e) { return false; }
  }

  async function klaviyoKnowsShopper() {
    if (knownState !== null) return knownState;
    if (!window.klaviyo || typeof window.klaviyo.isIdentified !== 'function') return null;
    try { knownState = !!(await withTimeout(window.klaviyo.isIdentified(), 5000)); } catch (e) { return null; }
    return knownState;
  }

  async function buildProps() {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    if (!cart.items || !cart.items.length) return null;
    const top = cart.items.slice().sort((a, b) => b.final_line_price - a.final_line_price)[0];
    let compareAt = top.final_price / 100;
    try {
      const prod = await fetch('/products/' + top.handle + '.js').then((r) => r.json());
      const v = (prod.variants || []).find((x) => x.id === top.variant_id);
      if (v && v.compare_at_price) compareAt = v.compare_at_price / 100;
    } catch (e) { /* keep fallback = price, so the template's strike-through simply doesn't show */ }
    const abs = (u) => (u && u.indexOf('//') === 0 ? 'https:' + u : u);
    return {
      'Product Name': top.product_title,
      ProductID: String(top.product_id),
      VariantID: String(top.variant_id),
      'Variant Name': top.variant_title || '',
      'Variant SKU': top.sku || '',
      ImageURL: abs(top.image) || '',
      URL: location.origin + top.url,
      Price: top.final_price / 100,
      CompareAtPrice: compareAt,
      Quantity: top.quantity,
      Brand: 'ZERO Footwear',
      ItemCount: cart.item_count,
      Items: cart.items.map((i) => ({
        'Product Name': i.product_title, 'Variant Name': i.variant_title || '', Quantity: i.quantity,
        Price: i.final_price / 100, ImageURL: abs(i.image) || '', URL: location.origin + i.url, VariantID: String(i.variant_id)
      })),
      extra: { checkout_url: location.origin + '/cart/' + cart.items.map((i) => i.variant_id + ':' + i.quantity).join(',') },
      $value: cart.total_price / 100,
      Source: 'Cart drawer - Save your cart'
    };
  }

  function subscribe(email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-10-15' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: 'Cart drawer - Save your cart',
        profile: { data: { type: 'profile', attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
      }, relationships: { list: { data: { type: 'list', id: EMAIL_LIST_ID } } } } })
    });
  }

  function bind(box) {
    if (box.dataset.bound) return;
    box.dataset.bound = '1';
    const form = box.querySelector('form');
    const input = box.querySelector('input[type="email"]');
    const btn = box.querySelector('button[type="submit"]');
    const msg = box.querySelector('.zero-cart__save-msg');
    const say = (text, isError) => { msg.textContent = text; msg.hidden = false; msg.classList.toggle('is-error', !!isError); };
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { say('Please enter a valid email address.', true); input.focus(); return; }
      btn.disabled = true; btn.textContent = 'Saving…';
      try {
        const props = await buildProps();
        if (!props) throw new Error('empty cart');
        await withTimeout(window.klaviyo.identify({ email: email }), 8000);
        await subscribe(email).catch(() => {});
        await withTimeout(window.klaviyo.track('Saved Cart', props), 8000);
        try { sessionStorage.setItem(DONE_KEY, '1'); } catch (err) { /* private mode */ }
        knownState = true;
        form.hidden = true;
        box.querySelector('.zero-cart__save-note').hidden = true;
        say('Saved! We’ll email you a link to your cart.');
      } catch (err) {
        btn.disabled = false; btn.textContent = 'Save my cart';
        say('Sorry, that didn’t save. Please try again.', true);
      }
    });
  }

  async function refresh() {
    const box = document.querySelector('[data-zero-save-cart]');
    if (!box) return;
    if (saved()) return; // already saved this visit; leave hidden
    const known = await klaviyoKnowsShopper();
    if (known === false) { box.hidden = false; bind(box); }
  }

  // The drawer re-renders its HTML on every cart change, so re-check whenever its content changes.
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => { pending = false; refresh(); }, 150);
    }).observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
